import type { NavbarItem } from "../Types/NavbarItem";
import {
  LegacyNavDeniedReason,
  type LegacyBlazorNavVisibility,
} from "../Types/LegacyBlazorNavVisibility";

const clientLabel = (clientName?: string): string => clientName ?? "This client";

const permissionMessage = (feature: string): string =>
  `You do not have permission to access ${feature}. To request access, please talk to your administrator`;

const notSubscribedMessage = (feature: string, clientName?: string): string =>
  `${clientLabel(clientName)} is not subscribed to ${feature}`;

const getQualityCalendarDisabledReasonText = (
  reason: LegacyNavDeniedReason | undefined,
  clientName?: string
): string => {
  switch (reason) {
    case LegacyNavDeniedReason.NotSubscribed:
    case LegacyNavDeniedReason.UnderDevelopment:
      return notSubscribedMessage("Quality Calendar", clientName);
    case LegacyNavDeniedReason.OrganisationDisabled:
      return "Calendar is not enabled for your organisation";
    case LegacyNavDeniedReason.PermissionDenied:
      return permissionMessage("Quality Calendar");
    default:
      return permissionMessage("Quality Calendar");
  }
};

const reasonToMessage = (
  reason: LegacyNavDeniedReason | undefined,
  featureName: string,
  clientName?: string,
  notOwnerMessage?: string
): string => {
  switch (reason) {
    case LegacyNavDeniedReason.UnderDevelopment:
      return "This feature is currently under development";
    case LegacyNavDeniedReason.PermissionDenied:
      return permissionMessage(featureName);
    case LegacyNavDeniedReason.NotSubscribed:
      return notSubscribedMessage(featureName, clientName);
    case LegacyNavDeniedReason.NotOwner:
      return notOwnerMessage ?? permissionMessage(featureName);
    default:
      return permissionMessage(featureName);
  }
};

/** Disabled tooltip for client-level KPI (navbar drawer + in-context Linked KPIs). */
export function getKpiNavDisabledReasonText(
  visibility: LegacyBlazorNavVisibility | null,
  clientName?: string
): string {
  return getLegacyNavDisabledReasonText({ id: 18 } as NavbarItem, visibility, clientName);
}

export function getLegacyNavDisabledReasonText(
  item: NavbarItem,
  visibility: LegacyBlazorNavVisibility | null,
  clientName?: string
): string {
  if (!visibility) {
    return "You do not currently have access to this item.";
  }

  switch (item.id) {
    case 17:
      return getQualityCalendarDisabledReasonText(
        visibility.qualityCalendarDeniedReason,
        clientName
      );
    case 18:
      return reasonToMessage(visibility.kpiDeniedReason, "KPI", clientName);
    case 9:
      return reasonToMessage(visibility.libraryDeniedReason, "Library", clientName);
    case 19:
      return reasonToMessage(visibility.libraryDeniedReason, "Library", clientName);
    case 20:
      return reasonToMessage(visibility.resourcesDeniedReason, "Resources", clientName);
    case 21:
      return permissionMessage("Notifications");
    case 22:
      return reasonToMessage(
        visibility.partnerControlsDeniedReason,
        "Partner Controls",
        clientName,
        "You are not the owner of a partner."
      );
    case 23:
      return reasonToMessage(
        visibility.departmentControlsDeniedReason,
        "Department Controls",
        clientName,
        "You are not the owner of a department."
      );
    default:
      return "You do not currently have access to this item.";
  }
}

export function getModulePermissionDeniedMessage(moduleName: string): string {
  return permissionMessage(moduleName);
}

export function getModuleNotSubscribedMessage(
  moduleName: string,
  clientName?: string
): string {
  return notSubscribedMessage(moduleName, clientName);
}

export function getFeatureNotEnabledMessage(clientName?: string): string {
  return `This feature is not enabled for ${clientLabel(clientName)}.`;
}

export function getAdminDisabledMessage(clientName?: string): string {
  return `You are not an administrator of ${clientLabel(clientName)}`;
}
